<script setup lang="ts">
/**
 * CATALOGUE de la vitrine boutique : filtre par catégorie + grille produits +
 * rappel panier + barre panier flottante mobile.
 *
 * ⚠️ CE COMPOSANT N'EST PLUS UNE « ÉTAPE » (chantier « panier »).
 * Il portait auparavant, en plus de la grille, le BLOC PANIER complet (lignes
 * modifiables, sous-total, frais de port annoncés, bouton « Continuer ») en bas
 * de l'étape 1 du tunnel. Ce bloc a été retiré : le panier est désormais le
 * PANNEAU LATÉRAL (`BoutiqueCartDrawer`), accessible en permanence depuis
 * l'icône panier et ouvert automatiquement à chaque ajout.
 *
 * ⚠️ REFONTE — LE CATALOGUE N'EST PLUS DANS UNE CARTE.
 * La page l'enfermait dans un bloc blanc `rounded-3xl` avec un en-tête
 * « Les produits » barré d'un filet. Chaque carte produit était donc une carte
 * DANS une carte : trois surfaces empilées pour une seule information. La fiche
 * produit a tranché — l'information flotte, la carte est réservée à l'action —
 * et la grille suit désormais ce canon. Les seuls blocs encartés qui subsistent
 * ici sont le RAPPEL PANIER et la BARRE MOBILE, qui sont l'un et l'autre des
 * blocs d'action.
 */
// Import EXPLICITE : l'auto-import Nuxt de @vueuse/core casse en SSR
// (cf. note de components/shared/InfiniteScrollSentinel.vue).
import type { ShopProduct } from "~/types/shop";

// Montants et dates dans la langue de la page.
const { formatCurrency } = useFormatLangue();

const props = defineProps<{
  /** Slug de la boutique — URL réelle des fiches produit. */
  shopSlug: string;
  /** Mode widget embarqué : le hero n'est pas rendu, le panier reste ici. */
  isEmbed?: boolean;
  products: ShopProduct[];
  totalItems: number;
  subtotal: number;
  showRemainingStock: boolean;
  canBuy: boolean;
  /** Message affiché quand l'achat est fermé (fenêtre de vente / clôture). */
  saleClosedMessage: string | null;
}>();

defineEmits<{
  (e: "open-product", product: ShopProduct): void;
  /** L'acheteur veut passer commande → page panier (porté par la page). */
  (e: "continue"): void;
}>();

const { open: openCartDrawer } = useShopCartDrawer();

// ─── Filtre catégories (affiché si ≥ 2 catégories) ─────────────────
const categories = computed(() => {
  const set = new Set<string>();
  for (const p of props.products) {
    if (p.category?.trim()) set.add(p.category.trim());
  }
  return [...set];
});

/**
 * Nombre de produits par catégorie — affiché DANS chaque pastille du filtre.
 * Un filtre qui ne dit pas ce qu'il contient se clique à l'aveugle.
 */
const categoryCounts = computed(() => {
  const counts: Record<string, number> = {};
  for (const p of props.products) {
    const key = p.category?.trim();
    if (key) counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
});

const selectedCategory = ref<string | null>(null);
const showCategoryFilter = computed(() => categories.value.length >= 2);

const filteredProducts = computed(() => {
  if (!selectedCategory.value) return props.products;
  return props.products.filter(
    (p) => p.category?.trim() === selectedCategory.value,
  );
});

/** Boutique réellement vide vs simple filtre sans résultat — messages distincts. */
const shopIsEmpty = computed(() => props.products.length === 0);

/**
 * Barre panier flottante MOBILE — affichée dès qu'il y a un article.
 *
 * Elle n'a plus de « footer natif » à éviter (le bloc panier a quitté cette
 * page) : elle est donc le rappel permanent du panier sur mobile, où l'icône de
 * l'en-tête défile hors de l'écran. Le clic OUVRE le panneau au lieu de faire
 * défiler vers un bloc qui n'existe plus.
 */
const showMobileCartBar = computed(() => props.totalItems > 0);

const mobileCartButtonRef = ref<HTMLElement | null>(null);

function onOpenDrawer() {
  openCartDrawer(mobileCartButtonRef.value);
}
</script>

<template>
  <section
    aria-labelledby="boutique-catalogue-title"
    :class="isEmbed ? '@container' : undefined"
  >
    <!-- Achat fermé (hors fenêtre de vente) : vitrine consultable, message
         clair. Composant de statut PARTAGÉ avec /b, variante bandeau. -->
    <BslugStatusNotice
      v-if="saleClosedMessage"
      class="mb-6"
      variant="inline"
      tone="red"
      icon="i-lucide-clock"
      :message="saleClosedMessage"
    />

    <!-- Titre de section + accès panier sur la MÊME ligne. L'en-tête vivait
         auparavant dans la carte du catalogue, en `text-sm` derrière un filet :
         il pesait moins qu'un nom de produit. Ici il ouvre franchement la
         grille, à l'échelle des titres de section de la fiche produit
         (« Vous aimerez aussi », `text-lg md:text-xl`). -->
    <div class="flex items-center justify-between gap-3">
      <h2
        id="boutique-catalogue-title"
        class="text-lg md:text-xl font-bold tracking-tight text-[#2d2e5c] dark:text-white"
      >
        {{ $t("boutique.stepCatalogue.lesProduits") }}
        <span
          v-if="products.length > 0"
          class="ml-2 text-sm md:text-base font-medium text-[#717198] dark:text-gray-400"
        >{{ products.length }}</span>
      </h2>
      <!--
        Vendeur + Partage : ils vivent normalement dans la colonne de droite
        (avec « Détails de votre commande »). Cette colonne DISPARAÎT quand le
        panier est vide — sans quoi elle laisse une bande blanche sur tout le
        flanc droit. Ils se replient donc ici, sur la ligne du titre.
        Le panier, lui, reste ici en EMBED seulement (le hero n'y est pas rendu)
        et hors de `AppHeader`, pilule flottante partagée par tout le site qui
        ne doit pas hériter d'un panier de boutique.
      -->
      <div class="flex items-center gap-2 shrink-0">
        <slot name="header-actions" />
        <BoutiqueCartButton v-if="isEmbed" :total-items="totalItems" />
      </div>
    </div>

    <BoutiqueCategoryFilter
      v-if="showCategoryFilter"
      v-model="selectedCategory"
      :categories="categories"
      :counts="categoryCounts"
      :total-count="products.length"
      class="mt-4"
    />

    <!-- GRILLE — 2 colonnes dès le mobile (avant : 1 seule).
         La carte n'ayant plus ni fond ni bordure et la photo étant passée en
         `aspect-[4/5]`, deux vignettes tiennent désormais côte à côte sans que
         le nom ni le prix soient coupés — c'est ce qui bloquait avant. Une
         colonne unique obligeait à faire défiler un écran entier PAR produit,
         alors que toutes les vitrines mobiles (Shopify, Vinted, Decathlon)
         montrent deux produits de front. Jusqu'à 4 colonnes en `xl`, où une
         grille à 3 laissait des vignettes démesurées.
         `gap-y` plus grand que `gap-x` : sans cadre, c'est l'espace vertical
         seul qui sépare le prix d'un produit du nom du suivant.
         EMBED : les points de rupture `lg:`/`xl:` regardent la fenêtre — or dans
         l'iframe d'un site tiers la fenêtre EST l'iframe (880px sur un site
         d'asso classique, 300px dans une colonne latérale) : `lg:` ne se
         déclenchait jamais et la grille restait à 2 colonnes géantes. Les
         variantes `@lg:`/`@3xl:` (container queries) regardent la largeur
         réelle du catalogue (`@container` sur la section) : 2 colonnes en
         encart, 3 vers 512px, 4 dès 768px — comme sur donoor.org. -->
    <div
      v-if="filteredProducts.length > 0"
      class="grid grid-cols-2 gap-x-4 gap-y-8 md:gap-x-6 md:gap-y-10 mt-6"
      :class="isEmbed
        ? '@lg:grid-cols-3 @3xl:grid-cols-4'
        : (totalItems > 0
          ? 'lg:grid-cols-3 xl:grid-cols-3'
          : 'lg:grid-cols-3 xl:grid-cols-4')"
    >
      <BoutiqueProductCard
        v-for="product in filteredProducts"
        :key="product.id"
        :product="product"
        :show-remaining-stock="showRemainingStock"
        :can-buy="canBuy"
        :shop-slug="shopSlug"
        @open="$emit('open-product', product)"
      />
    </div>

    <!-- Boutique réellement vide : rien à proposer, aucune action possible.
         Encartée (aplat blanc PLEIN) : sans photo ni grille, un état vide qui
         flotte sur le fond de page n'a plus aucune assise visuelle. -->
    <div
      v-else-if="shopIsEmpty"
      class="flex flex-col items-center justify-center text-center px-6 py-14 mt-6 rounded-3xl bg-white dark:bg-[#141414] shadow-[0_1px_3px_rgba(45,46,92,0.12)]"
    >
      <UIcon name="i-lucide-package-open" class="size-10 text-gray-300 dark:text-gray-600 mb-3" />
      <p class="text-base font-bold text-[#2d2e5c] dark:text-white">
        {{ $t("boutique.stepCatalogue.aucunProduitDisponible") }}
      </p>
      <p class="text-sm text-[#717198] dark:text-gray-400 mt-1.5 max-w-sm">
        {{ $t("boutique.stepCatalogue.aucunProduitDisponibleDescription") }}
      </p>
    </div>

    <!-- Filtre catégorie sans résultat : l'issue est de réinitialiser. État
         DISTINCT du précédent — la boutique n'est pas vide, c'est la sélection
         qui l'est, et le message doit le dire pour ne pas faire fuir. -->
    <div
      v-else
      class="flex flex-col items-center justify-center text-center px-6 py-14 mt-6 rounded-3xl bg-white dark:bg-[#141414] shadow-[0_1px_3px_rgba(45,46,92,0.12)]"
    >
      <UIcon name="i-lucide-filter-x" class="size-10 text-gray-300 dark:text-gray-600 mb-3" />
      <p class="text-base font-bold text-[#2d2e5c] dark:text-white">
        {{ $t("boutique.stepCatalogue.aucunProduitCategorie", { categorie: selectedCategory }) }}
      </p>
      <p class="text-sm text-[#717198] dark:text-gray-400 mt-1.5 max-w-sm">
        {{ $t("boutique.stepCatalogue.categorieVide") }}
      </p>
      <button
        type="button"
        class="mt-5 inline-flex items-center gap-1.5 min-h-11 px-5 rounded-xl text-sm font-semibold accent-btn transition-colors"
        @click="selectedCategory = null"
      >
        <UIcon name="i-lucide-rotate-ccw" class="size-4" />
        {{ $t("boutique.stepCatalogue.voirTousLesProduits") }}
      </button>
    </div>

    <!--
      Pas de rappel panier en bas de catalogue (Karim 2026-08-09) : le panier
      était affiché à TROIS endroits — ici, dans la colonne de droite
      (« Détails de votre commande », avec le détail des lignes et le
      sous-total) et dans le panneau latéral. Sur desktop la colonne le porte
      déjà ; sur mobile, où cette colonne passe sous le catalogue, c'est la
      barre flottante ci-dessous qui l'assure en permanence.
    -->

    <!-- Réserve la hauteur de la barre flottante : elle est en `fixed`, donc
         hors du flux — sans cet espaceur elle recouvre la fin du contenu.
         Uniquement mobile et panier non vide, comme la barre elle-même. -->
    <div v-if="showMobileCartBar" class="lg:hidden h-[88px]" aria-hidden="true" />

    <!-- ─── Barre panier flottante MOBILE ─────────────────────────────
         Sur mobile, l'icône panier de l'en-tête défile hors champ dès la
         deuxième rangée de produits. Cette barre restitue en permanence
         nombre d'articles + sous-total, et OUVRE le panneau latéral. -->
    <!-- Glissement court (`translate-y-4`) et non `translate-y-full` : la
         barre flotte à 12px du bord, elle n'a plus tout l'écran à remonter. -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4"
      leave-active-class="transition-all duration-150 ease-in"
      leave-to-class="opacity-0 translate-y-4"
    >
      <!-- ⚠️ BARRE FLOTTANTE, PAS COLLÉE AU BORD (Karim 2026-08-23) :
           détachée des trois bords, arrondie et posée en ombre portée — elle
           se lit comme une carte qui survole le catalogue, pas comme un pied
           de page soudé à l'écran.
           `bottom` combine la marge ET l'encoche : `env(safe-area-inset-bottom)`
           seul collerait la barre au bord sur les iPhone sans encoche (valeur
           0), d'où le `calc(… + 0.75rem)` qui garantit l'écart dans tous les
           cas. La marge basse remplace donc l'ancien `pb-[env(…)]`. -->
      <div
        v-if="showMobileCartBar"
        class="lg:hidden fixed inset-x-3 z-40 rounded-2xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#141414] shadow-[0_8px_28px_rgba(45,46,92,0.18)]"
        style="bottom: calc(env(safe-area-inset-bottom, 0px) + 0.75rem)"
      >
        <div class="flex items-center gap-3 px-3 py-2.5">
          <button
            ref="mobileCartButtonRef"
            type="button"
            data-testid="btn-mobile-open-cart"
            class="flex items-center gap-2.5 min-h-11 flex-1 min-w-0 text-left"
            :aria-label="$t('boutique.cartButton.ariaOuvrir', { n: totalItems }, totalItems)"
            @click="onOpenDrawer"
          >
            <span class="relative shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100 dark:bg-[#1e1e1e]">
              <UIcon name="i-lucide-shopping-cart" class="size-5 text-[#2d2e5c] dark:text-white" />
              <!-- Pastille sur l'angle : ici le déclencheur est une icône
                   seule (44px), aucune place dans le flux. Le cerne blanc la
                   détache du fond gris clair de la vignette. -->
              <span
                class="absolute -top-1 -right-1 min-w-5 h-5 px-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold tabular-nums ring-2 ring-white dark:ring-[#141414]"
                :style="{ background: 'var(--accent, #212121)', color: 'var(--accent-text, #fff)' }"
              >
                {{ totalItems }}
              </span>
            </span>
            <span class="min-w-0">
              <span class="block text-[11px] text-[#717198] dark:text-gray-500">
                {{ $t("boutique.stepCatalogue.articlesVoirPanier", { n: totalItems }, totalItems) }}
              </span>
              <span class="block font-bold text-base text-[#2d2e5c] dark:text-white truncate">
                {{ subtotal > 0 ? formatCurrency(subtotal) : $t("boutique.commun.gratuit") }}
              </span>
            </span>
          </button>
          <button
            data-testid="btn-mobile-checkout"
            class="shrink-0 min-h-11 px-5 accent-btn rounded-xl text-sm font-semibold transition-opacity disabled:opacity-40"
            :disabled="!canBuy"
            @click="$emit('continue')"
          >
            {{ $t("boutique.commun.commander") }}
          </button>
        </div>
      </div>
    </Transition>
  </section>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
/* Survol en COULEUR PLEINE (règle `ui_no_bg_transparency`). */
.accent-btn:hover {
  background: color-mix(in srgb, var(--accent, #212121) 88%, #000);
}
</style>
